import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Board } from '@/chess/board';
import {
  FixedNav,
  Caption,
  Button,
  StatStrip,
  Divider,
  AnimatedNumber,
  PulseDot,
  ErrorMessageWithDebug,
  MODULE_LABEL_FR,
} from '@/ui';
import { useCardStats, useDueCards } from './hooks';
import type { DueCard, RevisionModule } from './types';

/**
 * Revision Cards — page liste (queue de révision SRS).
 *
 * Affichage 4 KPIs (`due_now`, `total_active`, `due_next_7d`, `archived_total`)
 * + barre de filtres par module (tous/opening/tactic/endgame/autopsy avec
 * accent couleur DESIGN.md §3) + grid de cards avec mini-board Chessground
 * viewOnly 120px + bandeau slug/module/failure_count + bouton « Réviser ».
 *
 * Si zéro carte due → message kid-friendly (CLAUDE.md §1 user = Léon 11 ans).
 *
 * Wiring backend : hooks `useDueCards(module)` + `useCardStats()` (cf. plan
 * `docs/plans/2026-05-20-revision-cards-module.md` §3).
 */

/**
 * Liste fermée des filtres module. `'all'` est la valeur par défaut (=
 * pas de paramètre `module` côté API → toutes les cartes dues). Les 4
 * autres sont les `RevisionModule` du contrat backend.
 */
type ModuleFilter = 'all' | RevisionModule;

const MODULE_FILTERS: readonly ModuleFilter[] = [
  'all',
  'opening',
  'tactic',
  'endgame',
  'autopsy',
] as const;

/**
 * Mapping filtre → accent couleur. Tailwind purge ne tolère pas les classes
 * construites dynamiquement (`text-${accent}-500`) → on stocke les chaînes
 * littérales. Cf. DESIGN.md §3 (opening=violet, tactic=cyan, endgame=amber,
 * autopsy=coral).
 */
const FILTER_STYLES: Record<
  ModuleFilter,
  { label: string; activeClass: string; inactiveClass: string; dotColor: string }
> = {
  all: {
    label: 'Tous',
    activeClass: 'border-paper-50 text-paper-50 bg-paper-50/10',
    inactiveClass: 'border-ink-700 text-ink-300 hover:border-paper-50/30 hover:text-paper-50',
    dotColor: 'var(--paper-50)',
  },
  opening: {
    label: 'Ouverture',
    activeClass: 'border-violet-500 text-violet-500 bg-violet-500/10',
    inactiveClass: 'border-ink-700 text-ink-300 hover:border-violet-500/30 hover:text-violet-500',
    dotColor: 'var(--violet-500)',
  },
  tactic: {
    label: 'Tactiques',
    activeClass: 'border-cyan-500 text-cyan-500 bg-cyan-500/10',
    inactiveClass: 'border-ink-700 text-ink-300 hover:border-cyan-500/30 hover:text-cyan-500',
    dotColor: 'var(--cyan-500)',
  },
  endgame: {
    label: 'Finale',
    activeClass: 'border-amber-500 text-amber-500 bg-amber-500/10',
    inactiveClass: 'border-ink-700 text-ink-300 hover:border-amber-500/30 hover:text-amber-500',
    dotColor: 'var(--amber-500)',
  },
  autopsy: {
    label: 'Autopsie',
    activeClass: 'border-coral-500 text-coral-500 bg-coral-500/10',
    inactiveClass: 'border-ink-700 text-ink-300 hover:border-coral-500/30 hover:text-coral-500',
    dotColor: 'var(--coral-500)',
  },
};

export function RevisionCardsPage(): JSX.Element {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<ModuleFilter>('all');
  const dueQ = useDueCards(filter === 'all' ? undefined : filter);
  const statsQ = useCardStats();

  const cards = dueQ.data?.cards ?? [];
  const count = dueQ.data?.count ?? 0;

  const stats = statsQ.data;

  const chips = useMemo(
    () => [
      { label: 'Dues', value: String(stats?.due_now ?? 0), color: 'var(--paper-50)' },
      { label: 'Actives', value: String(stats?.total_active ?? 0), color: 'var(--ink-300)' },
    ],
    [stats?.due_now, stats?.total_active],
  );

  return (
    <>
      <FixedNav chips={chips} />
      <main className="mx-auto max-w-[1280px] px-6 py-8 flex flex-col gap-6">
        <header className="flex flex-col gap-2">
          <div className="flex items-center gap-2.5">
            <PulseDot color="var(--paper-50)" size={8} />
            <Caption>Revision Cards</Caption>
          </div>
          <h1 className="font-display text-display-2 text-paper-50">Cartes à réviser</h1>
          <p className="text-body-sm text-ink-300">
            Tes erreurs récentes — un peu chaque jour, à ton rythme.
          </p>
        </header>

        {/* KPI strip */}
        <StatStrip className="flex-wrap items-stretch gap-x-8">
          <StatTile
            label="Dues maintenant"
            value={stats?.due_now ?? 0}
            color="var(--paper-50)"
            testId="kpi-due-now"
          />
          <Divider vertical />
          <StatTile
            label="Total actives"
            value={stats?.total_active ?? 0}
            color="var(--ink-300)"
            testId="kpi-total-active"
          />
          <Divider vertical />
          <StatTile
            label="Dues 7 j"
            value={stats?.due_next_7d ?? 0}
            color="var(--cyan-500)"
            testId="kpi-due-next-7d"
          />
          <Divider vertical />
          <StatTile
            label="Archivées"
            value={stats?.archived_total ?? 0}
            color="var(--volt-500)"
            testId="kpi-archived-total"
          />
        </StatStrip>

        {/* Filtres module */}
        <nav
          aria-label="Filtre par module"
          className="flex flex-wrap gap-2"
          data-testid="module-filters"
        >
          {MODULE_FILTERS.map((f) => {
            const active = f === filter;
            const styles = FILTER_STYLES[f];
            const moduleCount =
              f === 'all'
                ? (stats?.due_now ?? 0)
                : (stats?.by_module?.[f]?.due_now ?? 0);
            return (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                aria-pressed={active}
                data-filter={f}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border font-mono text-xs transition-all duration-150 ${
                  active ? styles.activeClass : styles.inactiveClass
                }`}
              >
                <span
                  className="size-1.5 rounded-full"
                  style={{ backgroundColor: styles.dotColor }}
                  aria-hidden="true"
                />
                <span>{styles.label}</span>
                <span className="font-mono tabular-nums opacity-70">{moduleCount}</span>
              </button>
            );
          })}
        </nav>

        {/* États de chargement / erreur / liste vide */}
        {dueQ.isPending && (
          <p data-testid="loading" className="text-ink-300 italic">
            Chargement des cartes…
          </p>
        )}
        {dueQ.isError && (
          <ErrorMessageWithDebug
            className="text-coral-500"
            message="Erreur de chargement. Réessaie dans quelques secondes."
            debug="GET /api/v1/training/cards/due échoué"
          />
        )}
        {dueQ.data && cards.length === 0 && (
          <section
            data-testid="empty"
            className="glass glass-volt p-6 rounded-xl flex flex-col gap-3 max-w-xl"
          >
            <Caption color="volt">Bravo</Caption>
            <h2 className="font-display text-heading-2 text-paper-50">
              Aucune carte due aujourd'hui — repasse demain !
            </h2>
            <p className="text-body-sm text-ink-300">
              Tu peux continuer ton entraînement quotidien depuis Mission Control.
            </p>
            <div className="mt-2">
              <Button variant="primary" accent="volt" onClick={() => navigate('/')}>
                Retour Mission Control
              </Button>
            </div>
          </section>
        )}

        {/* Grid de cards */}
        {dueQ.data && cards.length > 0 && (
          <ul
            data-testid="cards-grid"
            data-count={count}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 list-none p-0"
          >
            {cards.map((card, i) => (
              <li key={card.id}>
                <CardTile
                  card={card}
                  delay={i * 40}
                  onReview={() =>
                    navigate(`/revision-cards/drill/${card.id}`)
                  }
                />
              </li>
            ))}
          </ul>
        )}

        <footer className="pt-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-caption uppercase text-ink-300 hover:text-paper-50 transition-all duration-150"
          >
            ← Mission Control
          </button>
        </footer>
      </main>
    </>
  );
}

// ─── Sous-composants ───────────────────────────────────────────────────────

function StatTile({
  label,
  value,
  color,
  testId,
}: {
  label: string;
  value: number;
  color: string;
  testId?: string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1 min-w-[100px]" {...(testId ? { 'data-testid': testId } : {})}>
      <Caption>{label}</Caption>
      <AnimatedNumber
        value={value}
        className="font-mono"
        style={{ fontSize: 26, color, lineHeight: 1, fontWeight: 500 }}
      />
    </div>
  );
}

/**
 * Card tile = mini-board Chessground (viewOnly ~140px) + bandeau métadonnées
 * + bouton « Réviser ». L'accent couleur suit le module (cf. FILTER_STYLES).
 */
function CardTile({
  card,
  delay,
  onReview,
}: {
  card: DueCard;
  delay: number;
  onReview: () => void;
}): JSX.Element {
  const moduleStyle = FILTER_STYLES[card.module];
  return (
    <article
      data-testid={`card-tile-${card.id}`}
      data-module={card.module}
      style={{ animationDelay: `${delay}ms` }}
      className="flex flex-col gap-3 p-4 bg-ink-800 border border-ink-700 rounded-xl animate-card-enter transition-all duration-150 hover:border-paper-50/30"
    >
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="size-1.5 rounded-full"
          style={{ backgroundColor: moduleStyle.dotColor }}
        />
        <span
          className="font-mono text-[11px] uppercase tracking-wider"
          style={{ color: moduleStyle.dotColor }}
        >
          {MODULE_LABEL_FR[card.module] ?? card.module}
        </span>
        <span className="ml-auto font-mono text-[11px] text-ink-300 tabular-nums">
          {card.failure_count} échec{card.failure_count > 1 ? 's' : ''}
        </span>
      </div>

      <div
        className="mx-auto"
        style={{ width: 140, height: 140 }}
        data-testid={`mini-board-${card.id}`}
      >
        <Board
          fen={card.fen}
          orientation={card.side_to_move}
          interactive={false}
        />
      </div>

      <div className="flex flex-col gap-1 min-h-[44px]">
        <h3 className="font-display text-sm text-paper-50 line-clamp-2">
          {card.display_label}
        </h3>
        <span className="font-mono text-[11px] text-ink-300">
          Trait aux {card.side_to_move === 'white' ? 'Blancs' : 'Noirs'}
        </span>
      </div>

      <Button
        variant="primary"
        size="compact"
        {...(card.module === 'opening'
          ? { accent: 'violet' as const }
          : card.module === 'tactic'
            ? { accent: 'cyan' as const }
            : card.module === 'endgame'
              ? { accent: 'amber' as const }
              : { accent: 'coral' as const })}
        onClick={onReview}
      >
        Réviser
      </Button>
    </article>
  );
}

export default RevisionCardsPage;
